/**
 * Devices helpers
 */
import { DeviceListType, DeviceListTypeId } from 'utils/enums/device';
import { getValueIfExists, propertyExist } from './propertyValidation';

export const allLocationsDefault = 'All Locations';
export const complementaryDeviceGroupIds = [
  DeviceListTypeId.Ats,
  DeviceListTypeId.Inverter,
  DeviceListTypeId.Gid,
  DeviceListTypeId.LocalIO,
  DeviceListTypeId.Meters,
];

export const deviceGroupTypeMembership = {
  [DeviceListTypeId.AcLoad]: [
    DeviceListTypeId.Ats,
    DeviceListTypeId.Inverter,
    DeviceListTypeId.Meters,
  ],
  [DeviceListTypeId.Ats]: [DeviceListTypeId.Ats, DeviceListTypeId.Meters],
  [DeviceListTypeId.Battery]: [DeviceListTypeId.Battery, 'BDC', DeviceListTypeId.Meters],
  [DeviceListTypeId.CustomerLoad]: [
    DeviceListTypeId.Ats,
    DeviceListTypeId.Inverter,
    DeviceListTypeId.Meters,
  ],
  [DeviceListTypeId.DcLoad]: [DeviceListTypeId.Ats, 'DCDC', DeviceListTypeId.Meters],
  [DeviceListTypeId.Gid]: [DeviceListTypeId.Ats, DeviceListTypeId.Meters, DeviceListTypeId.Gid],
  [DeviceListTypeId.LocalIO]: [DeviceListTypeId.LocalIO],
  [DeviceListTypeId.Meters]: [DeviceListTypeId.Meters],
  [DeviceListTypeId.Solar]: [DeviceListTypeId.Meters, DeviceListTypeId.Solar],
  [DeviceListTypeId.Rengine]: [
    DeviceListTypeId.Ats,
    DeviceListTypeId.Inverter,
    DeviceListTypeId.Meters,
    DeviceListTypeId.Rengine,
  ],
  [DeviceListTypeId.FrontOfGiDSolar]: [DeviceListTypeId.Inverter, DeviceListTypeId.Meters],
  [DeviceListTypeId.Electrolyzer]: [
    DeviceListTypeId.Electrolyzer,
    DeviceListTypeId.Inverter,
    DeviceListTypeId.Meters,
  ],
  [DeviceListTypeId.FuelCell]: [
    DeviceListTypeId.FuelCell,
    DeviceListTypeId.Inverter,
    DeviceListTypeId.Meters,
  ],
  [DeviceListTypeId.MicroTurbine]: [
    DeviceListTypeId.Inverter,
    DeviceListTypeId.MicroTurbine,
    DeviceListTypeId.Meters,
  ],
  DCENTRIQ: ['DCENTRIQ', DeviceListTypeId.Meters],
};

/**
 * getAllLocations
 * Get from devices list available locations.
 * @param {Array} devicesList Array with location inside
 * @param {String} containerName name to sen on each location element.
 * @returns {Array} an array with all locations based on input
 */
export const getAllLocations = (devicesList = [], containerName = '') => {
  let locations = [];

  if (devicesList.length) {
    const uniqueLocations = new Set(
      devicesList
        .filter((deviceItem) => propertyExist(() => deviceItem.lcmLocation))
        .map((deviceItem) => deviceItem.lcmLocation),
    );

    if (uniqueLocations && uniqueLocations.size) {
      uniqueLocations.delete('undefined');
      locations = [...uniqueLocations].sort().map((lcmLocation) => ({
        value: lcmLocation,
        text: `${containerName} ${lcmLocation}`,
      }));
    }
  }

  locations.unshift({
    value: allLocationsDefault,
    text: allLocationsDefault,
  });

  return locations;
};

export const isComplementaryDeviceGroup = (deviceGroupId) =>
  complementaryDeviceGroupIds.includes(deviceGroupId);

export const isComplementaryDeviceType = (device) =>
  complementaryDeviceGroupIds.includes(getValueIfExists(() => device.deviceType.name, null));

export const filterPrimaryGroupDevices = (deviceGroupId, devices) => {
  const groupDevices = Array.isArray(devices) ? devices : [];
  const acceptedTypes = deviceGroupTypeMembership[deviceGroupId];
  if (!acceptedTypes) {
    return groupDevices;
  }

  return groupDevices.filter((device) => {
    const deviceType = getValueIfExists(() => device.deviceType.name, deviceGroupId);
    return acceptedTypes.includes(deviceType);
  });
};

export const getFlattenedSubgroupDevices = (group = {}) =>
  (Array.isArray(group.subgroups) ? group.subgroups : []).flatMap((subgroup) => {
    const subgroupDevices = Array.isArray(subgroup?.devices) ? subgroup.devices : [];

    return subgroupDevices.map((device) =>
      device?.subgroupName ? device : { ...device, subgroupName: subgroup?.subgroupName },
    );
  });

export const scoreDeviceForDisplay = (device) => {
  if (!device || typeof device !== 'object') {
    return 0;
  }

  let score = 0;
  [
    'make',
    'model',
    'serialNo',
    'serialNumber',
    'firmwareVersion',
    'uniqueIdentifier',
    'deviceStatusId',
    'deviceGroupId',
    'componentName',
    'subgroupName',
    'deviceGroupIdName',
  ].forEach((field) => {
    if (device[field] !== null && device[field] !== undefined && device[field] !== '') {
      score += 1;
    }
  });

  if (Array.isArray(device.points)) {
    score += device.points.filter(
      (point) => point?.value !== null && point?.value !== undefined && point?.value !== '',
    ).length;
  }

  return score;
};

export const dedupeDevicesByScore = (devices = []) => {
  const dedupedDevices = [];
  const deviceIndexByKey = new Map();

  devices.forEach((device) => {
    if (!device) {
      return;
    }

    const deviceKey =
      device.deviceId ??
      device.id ??
      device.uniqueIdentifier ??
      device.uniqueId ??
      device.deviceName;

    if (deviceKey === null || deviceKey === undefined) {
      dedupedDevices.push(device);
      return;
    }

    const normalizedKey = `${deviceKey}`;
    const existingIndex = deviceIndexByKey.get(normalizedKey);

    if (existingIndex === undefined) {
      deviceIndexByKey.set(normalizedKey, dedupedDevices.length);
      dedupedDevices.push(device);
      return;
    }

    const existingDevice = dedupedDevices[existingIndex];
    if (scoreDeviceForDisplay(device) > scoreDeviceForDisplay(existingDevice)) {
      dedupedDevices[existingIndex] = device;
    }
  });

  return dedupedDevices;
};

export const sortDevicesByName = (devices = []) =>
  [...devices].sort((a, b) =>
    getValueIfExists(() => a.deviceName, '').localeCompare(
      getValueIfExists(() => b.deviceName, ''),
      'en',
      { numeric: true },
    ),
  );

export const getComplementaryGroupDevices = (devicesList, selectedGroupId, subgroupId) => {
  if (!isComplementaryDeviceGroup(selectedGroupId) || !Array.isArray(devicesList)) {
    return [];
  }

  const devices = devicesList.flatMap((group) => {
    const groupMatch = group?.deviceGroupName === selectedGroupId;
    const groupDevices = Array.isArray(group?.devices) ? group.devices : [];
    const subgroupDevices = getFlattenedSubgroupDevices(group);
    const shouldUseSubgroupDevices = propertyExist(() => subgroupId) || groupDevices.length === 0;
    const devicesToScan = shouldUseSubgroupDevices ? subgroupDevices : groupDevices;

    return devicesToScan.filter((device) => {
      const typeMatch = device?.deviceType?.name === selectedGroupId;

      if (!groupMatch && !typeMatch) {
        return false;
      }

      if (propertyExist(() => subgroupId) && device?.subgroupName !== subgroupId) {
        return false;
      }

      return true;
    });
  });

  return sortDevicesByName(dedupeDevicesByScore(devices));
};

/**
 * @method getValueFromSelectedPoint
 * Returns a DeviceListType Id or an empty object.
 * @param {string} deviceGroupId This is a string id to find into the catalog
 * @returns {object} Found device id in DeviceListType
 */
export const findDeviceListTypeById = (deviceGroupId) => {
  const foundDeviceType = DeviceListType.find((deviceType) => deviceType.id === deviceGroupId);
  return foundDeviceType || {};
};

/**
 * @method getValueFromSelectedPoint
 * It will give you the value from the require point
 * @param {array} points array of points to search
 * @param {string} pointName is an string that represents the Id for the point name
 * @return {object} Returns the value from the selected point
 */
export const getValueFromSelectedPoint = (points, pointName) => {
  let valuePoint;

  if (getValueIfExists(() => points.length, 0)) {
    const selectedPoint = points.find((point) => point.name === pointName);
    return getValueIfExists(() => selectedPoint.value, valuePoint);
  }

  return valuePoint;
};
